import { useState, useEffect, useRef, useCallback } from 'react'
import { Modal, Button, Alert, Spinner, Badge } from 'react-bootstrap'
import Quagga from '@ericblade/quagga2'

interface BarcodeScannerProps {
  show: boolean
  onClose: () => void
  onScan: (macAddress: string) => void
}

// TypeScript types for native BarcodeDetector API
interface DetectedBarcode {
  rawValue: string
  format: string
  boundingBox: DOMRectReadOnly
  cornerPoints: { x: number; y: number }[]
}

interface BarcodeDetectorOptions {
  formats?: string[]
}

declare global {
  interface Window {
    BarcodeDetector?: {
      new(options?: BarcodeDetectorOptions): BarcodeDetectorInstance
      getSupportedFormats(): Promise<string[]>
    }
  }
}

interface BarcodeDetectorInstance {
  detect(image: ImageBitmapSource): Promise<DetectedBarcode[]>
}

// Yealink MAC address prefixes (OUI)
const YEALINK_OUIS = [
  '805EC0', '805ec0',  // 80:5E:C0 - Yealink
  '001565',            // 00:15:65 - Yealink
  '7C2F80', '7c2f80',  // 7C:2F:80 - Yealink
  '249AD8', '249ad8',  // 24:9A:D8 - Yealink
  'C4FC22', 'c4fc22',  // C4:FC:22 - Yealink
]

// Viewfinder dimensions as percentage of scanner container
// Wider than tall to match barcode proportions (CODE_128 is horizontal)
const VIEWFINDER = {
  WIDTH_PERCENT: 80,
  HEIGHT_PERCENT: 30,
}

type ScannerType = 'native' | 'fallback' | null

// Viewfinder overlay sub-component
function ViewfinderOverlay() {
  const topBottom = `${(100 - VIEWFINDER.HEIGHT_PERCENT) / 2}%`
  const leftRight = `${(100 - VIEWFINDER.WIDTH_PERCENT) / 2}%`

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 10,
    }}>
      {/* Semi-transparent overlay - top */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: topBottom,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }} />
      {/* Semi-transparent overlay - bottom */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: topBottom,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }} />
      {/* Semi-transparent overlay - left */}
      <div style={{
        position: 'absolute',
        top: topBottom,
        left: 0,
        width: leftRight,
        bottom: topBottom,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }} />
      {/* Semi-transparent overlay - right */}
      <div style={{
        position: 'absolute',
        top: topBottom,
        right: 0,
        width: leftRight,
        bottom: topBottom,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }} />

      {/* Viewfinder frame */}
      <div style={{
        position: 'absolute',
        top: topBottom,
        left: leftRight,
        right: leftRight,
        bottom: topBottom,
        border: '2px solid rgba(255, 255, 255, 0.8)',
        borderRadius: '8px',
      }}>
        {/* Corner accents */}
        <div style={{
          position: 'absolute', top: -2, left: -2,
          width: 24, height: 24,
          borderTop: '4px solid #0d6efd',
          borderLeft: '4px solid #0d6efd',
          borderTopLeftRadius: '8px',
        }} />
        <div style={{
          position: 'absolute', top: -2, right: -2,
          width: 24, height: 24,
          borderTop: '4px solid #0d6efd',
          borderRight: '4px solid #0d6efd',
          borderTopRightRadius: '8px',
        }} />
        <div style={{
          position: 'absolute', bottom: -2, left: -2,
          width: 24, height: 24,
          borderBottom: '4px solid #0d6efd',
          borderLeft: '4px solid #0d6efd',
          borderBottomLeftRadius: '8px',
        }} />
        <div style={{
          position: 'absolute', bottom: -2, right: -2,
          width: 24, height: 24,
          borderBottom: '4px solid #0d6efd',
          borderRight: '4px solid #0d6efd',
          borderBottomRightRadius: '8px',
        }} />

        {/* Animated scan line */}
        <div style={{
          position: 'absolute',
          left: '10%',
          right: '10%',
          height: '2px',
          backgroundColor: '#0d6efd',
          boxShadow: '0 0 8px rgba(13, 110, 253, 0.6)',
          animation: 'scanLine 2s ease-in-out infinite',
        }} />
      </div>

      {/* Hint text below frame */}
      <div style={{
        position: 'absolute',
        bottom: `calc(${topBottom} - 32px)`,
        left: 0,
        right: 0,
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '0.85rem',
        fontWeight: 500,
        textShadow: '0 1px 3px rgba(0, 0, 0, 0.8)',
      }}>
        Barcode hier ausrichten
      </div>
    </div>
  )
}

export default function BarcodeScanner({ show, onClose, onScan }: BarcodeScannerProps) {
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [scannedValue, setScannedValue] = useState<string | null>(null)
  const [rawScannedValue, setRawScannedValue] = useState<string | null>(null)
  const [scannerType, setScannerType] = useState<ScannerType>(null)
  const [torchSupported, setTorchSupported] = useState(false)
  const [torchOn, setTorchOn] = useState(false)

  // Native BarcodeDetector refs
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const detectorRef = useRef<BarcodeDetectorInstance | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // Quagga2 handler ref (must be stable for offDetected)
  const quaggaHandlerRef = useRef<((result: any) => void) | null>(null)
  const scannerActiveRef = useRef(false)

  const scannerContainerId = 'barcode-scanner-container'

  const isNativeSupported = useCallback(() => {
    return 'BarcodeDetector' in window && window.BarcodeDetector !== undefined
  }, [])

  useEffect(() => {
    if (show) {
      startScanner()
    }

    return () => {
      stopScanner()
    }
  }, [show])

  const startScanner = async () => {
    setError(null)
    setScannedValue(null)
    setRawScannedValue(null)
    setScanning(true)
    setTorchOn(false)
    setTorchSupported(false)

    // Wait for modal to render
    await new Promise(resolve => setTimeout(resolve, 100))

    if (isNativeSupported()) {
      await startNativeScanner()
    } else {
      await startFallbackScanner()
    }
  }

  const startNativeScanner = async () => {
    try {
      setScannerType('native')
      console.log('Using native BarcodeDetector API')

      const supportedFormats = await window.BarcodeDetector!.getSupportedFormats()
      console.log('Supported barcode formats:', supportedFormats)

      const formats = ['code_128', 'code_39', 'qr_code', 'data_matrix', 'ean_13', 'ean_8']
        .filter(f => supportedFormats.includes(f))

      detectorRef.current = new window.BarcodeDetector!({ formats })

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        }
      })
      streamRef.current = stream

      // Apply continuous autofocus and check torch support
      const track = stream.getVideoTracks()[0]
      try {
        await track.applyConstraints({
          // @ts-ignore - focusMode supported on Android Chrome but not in TS types
          focusMode: 'continuous'
        })
      } catch (e) {
        console.debug('Continuous autofocus not supported:', e)
      }

      const capabilities = (track as any).getCapabilities?.()
      if (capabilities?.torch) {
        setTorchSupported(true)
      }

      // Create and setup video element
      const container = document.getElementById(scannerContainerId)
      if (!container) throw new Error('Scanner container not found')

      const video = document.createElement('video')
      video.srcObject = stream
      video.setAttribute('playsinline', 'true')
      video.style.width = '100%'
      video.style.height = '100%'
      video.style.objectFit = 'cover'
      video.style.borderRadius = '8px'

      container.innerHTML = ''
      container.appendChild(video)
      videoRef.current = video

      // Create offscreen canvas for region cropping
      canvasRef.current = document.createElement('canvas')

      await video.play()

      scannerActiveRef.current = true
      detectBarcodes()

    } catch (err) {
      console.error('Native scanner error:', err)
      handleScannerError(err)
    }
  }

  const detectBarcodes = async () => {
    if (!detectorRef.current || !videoRef.current || !scannerActiveRef.current) return

    try {
      const video = videoRef.current
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        const vw = video.videoWidth
        const vh = video.videoHeight

        // Crop to viewfinder region to avoid detecting barcodes outside the target area
        const cropX = vw * (100 - VIEWFINDER.WIDTH_PERCENT) / 200
        const cropY = vh * (100 - VIEWFINDER.HEIGHT_PERCENT) / 200
        const cropW = vw * VIEWFINDER.WIDTH_PERCENT / 100
        const cropH = vh * VIEWFINDER.HEIGHT_PERCENT / 100

        const canvas = canvasRef.current!
        canvas.width = cropW
        canvas.height = cropH
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH)

        const barcodes = await detectorRef.current.detect(canvas)

        if (barcodes.length > 0) {
          console.log('Native detector found barcodes:', barcodes)
          handleScannedValue(barcodes[0].rawValue)
          return
        }
      }
    } catch (err) {
      console.debug('Detection frame error:', err)
    }

    if (scannerActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(detectBarcodes)
    }
  }

  const startFallbackScanner = async () => {
    try {
      setScannerType('fallback')
      console.log('Using Quagga2 fallback library')

      const container = document.getElementById(scannerContainerId)
      if (!container) throw new Error('Scanner container not found')

      container.innerHTML = ''

      const topBottomPercent = ((100 - VIEWFINDER.HEIGHT_PERCENT) / 2).toFixed(0) + '%'
      const leftRightPercent = ((100 - VIEWFINDER.WIDTH_PERCENT) / 2).toFixed(0) + '%'

      await new Promise<void>((resolve, reject) => {
        Quagga.init({
          inputStream: {
            type: 'LiveStream',
            target: container,
            constraints: {
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            area: {
              top: topBottomPercent,
              right: leftRightPercent,
              bottom: topBottomPercent,
              left: leftRightPercent,
            },
          },
          decoder: {
            readers: [
              'code_128_reader',
              'code_39_reader',
            ],
            multiple: false,
          },
          locate: true,
          locator: {
            patchSize: 'medium',
            halfSample: true,
          },
          frequency: 10,
          numOfWorkers: navigator.hardwareConcurrency
            ? Math.min(navigator.hardwareConcurrency, 4)
            : 2,
        }, (err) => {
          if (err) {
            reject(err)
            return
          }
          resolve()
        })
      })

      // Style Quagga's video element
      const videoElement = container.querySelector('video')
      if (videoElement) {
        videoElement.style.width = '100%'
        videoElement.style.height = '100%'
        videoElement.style.objectFit = 'cover'
        videoElement.style.borderRadius = '8px'
      }

      // Hide Quagga's built-in canvas overlays (we use our own viewfinder)
      container.querySelectorAll('canvas').forEach(c => {
        c.style.display = 'none'
      })

      // Check torch support
      const track = Quagga.CameraAccess.getActiveTrack()
      if (track) {
        const capabilities = (track as any).getCapabilities?.()
        if (capabilities?.torch) {
          setTorchSupported(true)
        }
        // Apply continuous autofocus
        try {
          await track.applyConstraints({
            // @ts-ignore - focusMode supported on Android Chrome but not in TS types
            focusMode: 'continuous'
          })
        } catch (e) {
          console.debug('Continuous autofocus not supported:', e)
        }
      }

      // Register detection handler
      quaggaHandlerRef.current = (result: any) => {
        const code = result.codeResult?.code
        if (code) {
          console.log('Quagga2 detected:', code, 'format:', result.codeResult.format)
          handleScannedValue(code)
        }
      }
      Quagga.onDetected(quaggaHandlerRef.current)

      scannerActiveRef.current = true
      Quagga.start()
    } catch (err) {
      console.error('Quagga2 scanner error:', err)
      handleScannerError(err)
    }
  }

  const handleScannerError = (err: unknown) => {
    if (err instanceof Error) {
      if (err.message.includes('NotAllowedError') || err.message.includes('Permission denied')) {
        setError('Kamera-Zugriff verweigert. Bitte erlauben Sie den Zugriff in Ihren Browser-Einstellungen.')
      } else if (err.message.includes('NotFoundError') || err.message.includes('Requested device not found')) {
        setError('Keine Kamera gefunden. Bitte stellen Sie sicher, dass eine Kamera angeschlossen ist.')
      } else {
        setError(`Fehler beim Starten der Kamera: ${err.message}`)
      }
    } else {
      setError('Unbekannter Fehler beim Starten der Kamera')
    }
    setScanning(false)
  }

  const stopScanner = async () => {
    scannerActiveRef.current = false

    // Stop native scanner
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
      videoRef.current = null
    }

    detectorRef.current = null
    canvasRef.current = null

    // Stop Quagga2 fallback
    if (quaggaHandlerRef.current) {
      try {
        Quagga.offDetected(quaggaHandlerRef.current)
      } catch (err) {
        console.debug('Error removing Quagga handler:', err)
      }
      quaggaHandlerRef.current = null
    }

    if (scannerType === 'fallback') {
      try {
        Quagga.stop()
      } catch (err) {
        console.error('Error stopping Quagga2:', err)
      }
    }

    setScanning(false)
    setScannerType(null)
    setTorchOn(false)
    setTorchSupported(false)
  }

  const toggleTorch = useCallback(async () => {
    if (scannerType === 'native') {
      const track = streamRef.current?.getVideoTracks()[0]
      if (!track) return
      try {
        await track.applyConstraints({
          advanced: [{ torch: !torchOn } as any]
        })
        setTorchOn(!torchOn)
      } catch (err) {
        console.error('Torch toggle failed:', err)
      }
    } else if (scannerType === 'fallback') {
      try {
        const track = Quagga.CameraAccess.getActiveTrack()
        if (track) {
          await track.applyConstraints({
            advanced: [{ torch: !torchOn } as any]
          })
          setTorchOn(!torchOn)
        }
      } catch (err) {
        console.error('Quagga torch toggle failed:', err)
      }
    }
  }, [scannerType, torchOn])

  const handleScannedValue = (value: string) => {
    console.log('Scanned raw value:', value)
    setRawScannedValue(value)

    const macAddress = extractMacAddress(value)

    if (macAddress) {
      setScannedValue(macAddress)
      stopScanner()
    } else {
      setScannedValue(null)
      setError(`Konnte keine MAC-Adresse erkennen. Gescannter Wert: ${value}`)
    }
  }

  const extractMacAddress = (value: string): string | null => {
    const cleaned = value.replace(/\s/g, '')
    console.log('Extracting MAC from:', cleaned)

    // Pattern 1: MAC:XXXXXXXXXXXX or MAC=XXXXXXXXXXXX
    let match = cleaned.match(/MAC[=:]?([0-9A-Fa-f]{12})/i)
    if (match) {
      console.log('Matched pattern 1 (MAC:xxx):', match[1])
      return formatMacAddress(match[1])
    }

    // Pattern 2: Just 12 hex characters (common Yealink format)
    match = cleaned.match(/^([0-9A-Fa-f]{12})$/)
    if (match) {
      console.log('Matched pattern 2 (12 hex chars):', match[1])
      return formatMacAddress(match[1])
    }

    // Pattern 3: MAC address with separators (aa:bb:cc:dd:ee:ff or aa-bb-cc-dd-ee-ff)
    match = value.match(/([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}/)
    if (match) {
      console.log('Matched pattern 3 (with separators):', match[0])
      return match[0].toLowerCase().replace(/-/g, ':')
    }

    // Pattern 4: Look for Yealink OUI followed by 6 hex chars anywhere
    for (const oui of YEALINK_OUIS) {
      const ouiPattern = new RegExp(`(${oui}[0-9A-Fa-f]{6})`, 'i')
      match = cleaned.match(ouiPattern)
      if (match) {
        console.log('Matched pattern 4 (Yealink OUI):', match[1])
        return formatMacAddress(match[1])
      }
    }

    // Pattern 5: Look for any 12 consecutive hex chars
    match = cleaned.match(/([0-9A-Fa-f]{12})/)
    if (match) {
      console.log('Matched pattern 5 (any 12 hex):', match[1])
      return formatMacAddress(match[1])
    }

    // Pattern 6: Yealink box format - might have SN and MAC together
    match = cleaned.match(/([0-9A-Fa-f]{12})(?:[^0-9A-Fa-f]|$)/)
    if (match) {
      console.log('Matched pattern 6 (with delimiter):', match[1])
      return formatMacAddress(match[1])
    }

    console.log('No MAC pattern matched')
    return null
  }

  const formatMacAddress = (mac: string): string => {
    const hex = mac.toLowerCase()
    return `${hex.slice(0,2)}:${hex.slice(2,4)}:${hex.slice(4,6)}:${hex.slice(6,8)}:${hex.slice(8,10)}:${hex.slice(10,12)}`
  }

  const handleConfirm = () => {
    if (scannedValue) {
      onScan(scannedValue)
      handleClose()
    }
  }

  const handleUseRawValue = () => {
    if (rawScannedValue) {
      const formatted = formatMacAddress(rawScannedValue.replace(/[^0-9A-Fa-f]/g, '').slice(0, 12))
      if (formatted.length === 17) {
        onScan(formatted)
        handleClose()
      }
    }
  }

  const handleClose = () => {
    stopScanner()
    setScannedValue(null)
    setRawScannedValue(null)
    setError(null)
    onClose()
  }

  const handleRetry = () => {
    setScannedValue(null)
    setRawScannedValue(null)
    setError(null)
    startScanner()
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-upc-scan me-2"></i>
          MAC-Adresse scannen
          {scannerType && (
            <Badge
              bg={scannerType === 'native' ? 'success' : 'secondary'}
              className="ms-2"
              style={{ fontSize: '0.6rem', verticalAlign: 'middle' }}
            >
              {scannerType === 'native' ? 'Native API' : 'Fallback'}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="warning" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
            {rawScannedValue && (
              <div className="mt-2">
                <small className="text-muted">
                  Roher Wert: <code>{rawScannedValue}</code>
                </small>
              </div>
            )}
          </Alert>
        )}

        {!scannedValue ? (
          <>
            <Alert variant="info" className="mb-3">
              <i className="bi bi-info-circle me-2"></i>
              Halten Sie den Barcode auf der Unterseite des Yealink-Telefons oder der Verpackung vor die Kamera.
            </Alert>

            <div
              style={{
                position: 'relative',
                width: '100%',
                minHeight: '350px',
                backgroundColor: '#000',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <div
                id={scannerContainerId}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '350px',
                  position: 'relative',
                }}
              >
                {!scanning && !error && (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '350px' }}>
                    <Spinner animation="border" variant="light" />
                  </div>
                )}
              </div>

              {/* Viewfinder overlay */}
              {scanning && <ViewfinderOverlay />}

              {/* Torch toggle button */}
              {scanning && torchSupported && (
                <button
                  onClick={toggleTorch}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 20,
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: torchOn
                      ? 'rgba(255, 193, 7, 0.9)'
                      : 'rgba(255, 255, 255, 0.3)',
                    color: torchOn ? '#000' : '#fff',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backdropFilter: 'blur(4px)',
                    transition: 'background-color 0.2s',
                  }}
                  title={torchOn ? 'Taschenlampe aus' : 'Taschenlampe an'}
                >
                  <i className={`bi ${torchOn ? 'bi-lightbulb-fill' : 'bi-lightbulb'}`}></i>
                </button>
              )}
            </div>

            {scanning && (
              <div className="text-center mt-2">
                <small className="text-muted">
                  <i className="bi bi-camera-video me-1"></i>
                  Scanner aktiv - Barcode ins Bild halten
                </small>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
            <h5 className="mt-3">MAC-Adresse erkannt!</h5>
            <div className="my-4">
              <code style={{ fontSize: '1.5rem', padding: '0.5rem 1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {scannedValue}
              </code>
            </div>
            {rawScannedValue && rawScannedValue !== scannedValue.replace(/:/g, '') && (
              <p className="text-muted small">
                Gescannter Wert: <code>{rawScannedValue}</code>
              </p>
            )}
            <p className="text-muted">Möchten Sie diese MAC-Adresse verwenden?</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Abbrechen
        </Button>
        {scannedValue ? (
          <>
            <Button variant="outline-primary" onClick={handleRetry}>
              <i className="bi bi-arrow-repeat me-1"></i>
              Erneut scannen
            </Button>
            <Button variant="success" onClick={handleConfirm}>
              <i className="bi bi-check-lg me-1"></i>
              Übernehmen
            </Button>
          </>
        ) : (
          <>
            {error && rawScannedValue && (
              <Button variant="outline-warning" onClick={handleUseRawValue}>
                <i className="bi bi-exclamation-triangle me-1"></i>
                Rohen Wert verwenden
              </Button>
            )}
            {error && (
              <Button variant="primary" onClick={handleRetry}>
                <i className="bi bi-arrow-repeat me-1"></i>
                Erneut versuchen
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
